// libraries
import {
  Button,
  ButtonDesigns,
  ButtonTypes,
  ErrorList,
  getFontCss,
  styleConstants,
  TextInputField,
  themeConstants,
} from '@paywithextend/components'
import React from 'react'
import { useCognitoResetPasswordConfirm } from 'src/api/mutation/resetPassword'
import CodeVerification from 'src/components/CodeVerification/CodeVerification'
import PasswordInputField from 'src/components/PasswordInputField/PasswordInputField'
import { useFormFields } from 'src/hooks'
import { useTranslate } from 'src/localization/Trns'
import { parseBackendErrors } from 'src/utils/error.utils'
import {
  stringHasSpace,
  stringIsEmpty,
} from 'src/utils/validation/string.validation'
import { css } from 'styled-components'

interface ForgotPasswordFormProps {
  email: string
}

export type ForgotPasswordFormFlatFields = {
  emailCode: string
  newPassword: string
}

const ForgotPasswordForm: React.FC<ForgotPasswordFormProps> = (
  props,
): React.ReactElement => {
  const translate = useTranslate()

  const {
    mutate: cognitoResetPasswordConfirm,
    isPending: loading,
    error,
  } = useCognitoResetPasswordConfirm()

  // Error surface parity with the removed
  // POST_COGNITO_RESET_PASSWORD_CONFIRM_FAILURE reducer case, which computed
  // { topLevelError, errorDetailMap } via parseBackendErrors(err.response.data).
  // Memoized on `error` so errorDetailMap keeps a stable reference across renders
  // (the old Redux selector did) — useFormFields effects off it, so a fresh object
  // every render would re-fire them.
  const { topLevelError, errorDetailMap } = React.useMemo(() => {
    const errorPayload = error?.response?.data
    return errorPayload
      ? parseBackendErrors(errorPayload)
      : { topLevelError: undefined, errorDetailMap: undefined }
  }, [error])

  const onSubmit = (e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault()
    const passedFlatFieldsValidation = flatFieldsSetErrors()

    if (passedFlatFieldsValidation) {
      cognitoResetPasswordConfirm({
        code: flatFields.emailCode,
        email: props.email,
        newPassword: flatFields.newPassword,
      })
    }
  }

  const {
    fields: flatFields,
    fieldsErrors: flatFieldsErrors,
    onFieldChange: onFlatFieldChange,
    setErrorsAndReturnDidPassValidation: flatFieldsSetErrors,
  } = useFormFields<ForgotPasswordFormFlatFields>(
    {
      emailCode: '',
      newPassword: '',
    },
    () => ({
      emailCode: [stringIsEmpty(), stringHasSpace],
      newPassword: [stringIsEmpty(), stringHasSpace],
    }),
    errorDetailMap,
  )

  return (
    <form
      css={css`
        display: flex;
        flex-direction: column;
      `}
      onSubmit={onSubmit}
    >
      <div
        css={css`
          ${getFontCss(
            32,
            themeConstants.colors.font_primary,
            styleConstants.fontFamilies.medium,
          )};
          margin-bottom: 24px;
        `}
      >
        {translate('Verify Code and Change Password')}
      </div>
      {/* hidden email field for accessibility reasons */}
      <TextInputField
        css={css`
          display: none;
        `}
        value={props.email}
        placeholder={''}
        onChange={(): null => {
          return null
        }}
        type={'email'}
        autoComplete={'email'}
        errors={errorDetailMap?.['email']}
        inputAriaLabel={'email address'}
      />
      <CodeVerification
        value={flatFields.emailCode}
        onChange={(val: string): void => onFlatFieldChange('emailCode', val)}
        errors={flatFieldsErrors['emailCode']}
        email={props.email}
      />
      <div
        css={css`
          margin-bottom: 40px;
        `}
      >
        <PasswordInputField
          value={flatFields.newPassword}
          onChange={(val: string): void =>
            onFlatFieldChange('newPassword', val)
          }
          errors={flatFieldsErrors['newPassword']}
          fieldLabel={translate('New password')}
          isNewPassword
        />
      </div>
      <Button
        type={ButtonTypes.submit}
        design={ButtonDesigns.designCta}
        text={translate('Change Password')}
        loading={loading}
        // TODO: add validation for disabled state
        disabled={loading}
        big
      />
      {topLevelError !== undefined && <ErrorList errors={[topLevelError]} />}
    </form>
  )
}

export default ForgotPasswordForm
